import React, { useEffect, useState } from "react";
import { CompactTable } from "@table-library/react-table-library/compact";
import { useTheme } from "@table-library/react-table-library/theme";
import { getTheme } from "@table-library/react-table-library/baseline";
import { useSort } from "@table-library/react-table-library/sort";
import { BsSearch } from "react-icons/bs";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";

import {
  Table,
  Header,
  HeaderRow,
  Body,
  Row,
  HeaderCell,
  Cell,
} from "@table-library/react-table-library/table";

import { useLocation } from "react-router-dom";
import { ForkLeft } from "@mui/icons-material";

const TableComponent = ({
  searchString,
  setHashData,
  setIsTxData,
  setTableData,
  tableData,
}) => {
  const [data, setData] = useState({ nodes: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState("");

  const location = useLocation();
  const { pathname } = location;

  const resetTables = () => {
    setTableData({ nodes: [] });
    setData({ nodes: [] });
    setHashData({ nodes: [] });
    setSelectedAddress("");
  };

  async function fetchLocalJson() {
    try {
      resetTables();
      let response;
      switch (pathname) {
        case "/phase_1":
          response = await import("../assets/phase_1/data.json");
          break;
        case "/phase_2":
          response = await import("../assets/phase_2/data.json");
          break;
        default:
          response = await import("../assets/phase_1/data.json");
          break;
      }

      const jsonData = response.default;

      return jsonData;
    } catch (error) {
      console.error("Error fetching local JSON:", error);
      throw error;
    }
  }

  useEffect(() => {
    const localJsonUrl = "../assets/data.json";
    setIsLoading(true);
    fetchLocalJson(localJsonUrl)
      .then((d) => {
        // console.log("Fetched JSON data:", d);

        const values = Object.entries(d).map(([id, data]) => ({ id, ...data }));
        setData({ nodes: values });
      })
      .finally(() => setIsLoading(false))
      .catch((error) => {
        console.error("Error handling fetched JSON:", error);
      });
  }, [pathname]);

  const [isTableEmpty, setIsTableEmpty] = useState(false);

  useEffect(() => {
    setTableData({ nodes: [] });
    if (data.nodes.length > 0) {
      const newData = {
        nodes: data.nodes.filter((item) =>
          item.id.toLowerCase().includes(searchString.toLowerCase())
        ),
      };
      setIsTableEmpty(newData.nodes.length === 0);
      setTableData(newData);
    }
  }, [data, searchString]);

  function onSortChange(action, state) {
    // console.log(action, state);
  }

  const sort = useSort(
    data,
    {
      onChange: onSortChange,
    },
    {
      sortFns: {
        gasIncreasePct: (array) =>
          array.sort(
            (a, b) => a.gas_increase_percentage - b.gas_increase_percentage
          ),
        NumAlreadyFailedTxswithIncreasePct: (array) =>
          array.sort(
            (a, b) =>
              a.num_failed_txs_with_increase_percentage -
              b.num_failed_txs_with_increase_percentage
          ),
        NumTxsWithIncreasePct: (array) =>
          array.sort(
            (a, b) =>
              a.num_txs_with_increase_percentage -
              b.num_txs_with_increase_percentage
          ),
      },
    }
  );

  const theme = useTheme([
    getTheme(),
    {
      Table: `
        --data-table-library_grid-template-columns:  23% 19% 27% 21% 10%;
      `,
      HeaderRow: `
            background-color: #171717;
            color: #fff;
            line-height: 1em;
            text-align: center;
            width: 100%;
            text-wrap:wrap;
          `,
      Row: `
            &:nth-of-type(odd) {
              background-color: #000000;
            }
    
            &:nth-of-type(even) {
              background-color: #171717;
            }
            &:hover {
              background-color: #7fccbd;
              color: #000;

              a {
                background-color: #7fccbd;
                color: #000;
              }
            }

            &:target {
              color:red;
            }
            color: #7fccbd;
            line-height: 3em;
            text-align: right;
            width: 100%;
          `,
    },
  ]);

  const handleRowClick = (item, event) => {
    setSelectedAddress(item.id);
    if (item.tx_data) {
      setIsTxData(true);
      setHashData({ nodes: item.tx_data });
    } else {
      setHashData({ nodes: [] });
      setIsTxData(false);
    }
  };

  return (
    <div
      className={`tableWrapper ${
        tableData.nodes.length === 0 ? "centerText" : ""
      }`}
    >
      {tableData.nodes.length > 0 && (
        <Table
          data={tableData}
          theme={theme}
          layout={{ custom: true, horizontalScroll: false }}
        >
          {(tableList) => (
            <>
              <Header>
                <HeaderRow className="align-center">
                  <HeaderCell className="headCell">Address</HeaderCell>
                  <HeaderCell className="headCell">
                    <div className="headCell">
                      Gas Increase %
                      <Tooltip
                        title="compared to before Sirius activation, how much the gas used increases on average (considering the same load and type of txs as seen before activation)"
                        placement="bottom"
                      >
                        <InfoIcon
                          style={{ fontSize: "20px", paddingLeft: "5px" }}
                        />
                      </Tooltip>
                    </div>
                  </HeaderCell>
                  <HeaderCell>
                    <div className="headCell">
                      Failed txs with increase
                      <Tooltip
                        title="percentage of transactions with higher gas usage after Sirius activation (considering the same load and type of txs as seen before activation)"
                        placement="bottom"
                      >
                        <InfoIcon
                          style={{ fontSize: "20px", paddingLeft: "5px" }}
                        />
                      </Tooltip>
                    </div>
                  </HeaderCell>
                  <HeaderCell>
                    <div className="headCell">
                      Txs with increase
                      <Tooltip
                        title="percentage of transactions that will fail with out of gas after Sirius activation if the gas limit stays the same "
                        placement="bottom"
                      >
                        <InfoIcon
                          style={{ fontSize: "20px", paddingLeft: "5px" }}
                        />
                      </Tooltip>
                    </div>
                  </HeaderCell>
                  <HeaderCell className="explorerLnkColumn"></HeaderCell>
                </HeaderRow>
              </Header>

              <Body>
                {tableList.map((item, idx) => (
                  <Row
                    key={`${item.tx_hash}-${idx}`}
                    item={item}
                    onClick={handleRowClick}
                    className={`tableRow ${
                      item.id === selectedAddress ? "active" : ""
                    }`}
                  >
                    <Cell>{item.id}</Cell>
                    <Cell>{item.gas_increase_percentage}</Cell>
                    <Cell>
                      {item.num_failed_txs_with_increase_percentage === -1
                        ? 0
                        : item.num_failed_txs_with_increase_percentage}
                    </Cell>
                    <Cell>{item.num_txs_with_increase_percentage}</Cell>
                    <Cell className="alignCenter explorerLnkColumn">
                      <a
                        className="explorerLnk"
                        target="_blank"
                        href={`https://explorer.multiversx.com/search/${item.id}`}
                        rel="noreferrer"
                      >
                        <BsSearch />
                      </a>
                    </Cell>
                  </Row>
                ))}
              </Body>
            </>
          )}
        </Table>
      )}
      {isLoading && <h2>Data is Loading...</h2>}
      {/* <h3>Data is Loading...</h3> */}
      {isTableEmpty && searchString !== "" && (
        <h2>Address "{searchString}" not found!</h2>
      )}
    </div>
  );
};

export const MemoizedTable = React.memo(TableComponent);
